// NOKE 專櫃 barcode price hang-tag printing.
//
// Renders one Code 128 label per PHYSICAL PIECE (qty 3 => 3 labels) into a popup
// window and fires window.print(). Each barcode is rendered by JsBarcode into an
// inline <svg> in THIS document, serialized to static markup, and embedded in the
// popup HTML — so the barcodes are guaranteed painted before print() runs (no
// async image/canvas decode to race against).
//
// Label layout (portrait hang-tag):
//   品名 (product name, 2-line clamp)
//   SKU · 顏色/尺寸
//   NT$ price (prominent)
//   Code 128 barcode + human-readable code
//   ── divider ──
//   產地 / 進口商 / 地址 compliance fine print
//
// The document markup/CSS is produced by the PURE function
// buildNokeLabelsDocumentHtml() so a standalone preview can be generated from
// the exact same code path (no drift between preview and print popup).

import JsBarcode from 'jsbarcode';

// ---- Label stock dimensions (change here only) -----------------------------
// Portrait hang-tag, matched to Magnus's actual label stock (50x65mm).
export const LABEL_WIDTH_MM = 50;
export const LABEL_HEIGHT_MM = 65;

// ---- Compliance block (Taiwan labeling requirements) -----------------------
export const DEFAULT_ORIGIN = 'Made in Korea';
export const IMPORTER_NAME = '諾蘭國際股份有限公司';
export const IMPORTER_ADDRESS = '臺北市中山區南京東路3段26號地下1樓';

// A single label's data. One label is printed per piece; `copies` expands qty.
export interface NokeLabelData {
  sku: string;
  /** Full product title; brand/origin prefix is stripped for display. */
  productTitle?: string | null;
  color?: string;
  size?: string;
  price: number | string;
  barcode: string; // Code 128 payload, e.g. "NK123456"
  /** '中國' | 'Made in Korea' | ...; defaults to Made in Korea when absent. */
  origin?: string | null;
  copies?: number; // number of physical pieces => number of labels (default 1)
}

// Strip the storefront naming prefix (【Metanoia】🇰🇷韓製 / 【Metanoia】韓國設計)
// from the display name — it wastes label space and duplicates the 產地 line.
export const displayProductName = (title: string | null | undefined): string => {
  if (!title) return '';
  const stripped = title
    .replace(/^【[^】]*】\s*(?:🇰🇷\s*)?(?:韓製|韓國設計)?\s*/u, '')
    .trim();
  return stripped || title.trim();
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Serialize one Code 128 barcode to inline SVG markup using JsBarcode.
// Browser-only (uses the DOM); the Node preview generator supplies its own
// barcodeSvgFor implementation to buildNokeLabelsDocumentHtml().
function barcodeSvgMarkup(payload: string): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, payload, {
      format: 'CODE128',
      displayValue: true,
      fontSize: 12,
      height: 40,
      width: 1.6,
      margin: 4,
      textMargin: 2,
    });
  } catch (e) {
    // If the payload is somehow invalid for Code 128, fall back to plain text so
    // the label still prints something scannable-by-eye rather than throwing.
    return `<div class="barcode-fallback">${escapeHtml(payload)}</div>`;
  }
  return new XMLSerializer().serializeToString(svg);
}

function formatPrice(price: number | string): string {
  const n = typeof price === 'number' ? price : parseFloat(price);
  if (isNaN(n)) return String(price);
  return `NT$ ${Math.round(n).toLocaleString('en-US')}`;
}

// Build one label element as an HTML string.
function labelHtml(label: NokeLabelData, barcodeSvg: string): string {
  const name = displayProductName(label.productTitle);
  const metaParts = [label.color, label.size].filter(Boolean).map((p) => escapeHtml(String(p)));
  const meta = metaParts.join(' / ');
  const origin = escapeHtml((label.origin || '').trim() || DEFAULT_ORIGIN);
  // The inner markup lives in .label-content, which is translated by the
  // calibration offset (var --off-x/--off-y) so print position can be nudged
  // without changing the physical label/page size.
  return `
    <div class="label">
      <div class="label-content">
        <div class="name">${escapeHtml(name) || '&nbsp;'}</div>
        <div class="spec">
          <span class="sku">${escapeHtml(label.sku)}</span>
          ${meta ? `<span class="variant">${meta}</span>` : ''}
        </div>
        <div class="price">${escapeHtml(formatPrice(label.price))}</div>
        <div class="barcode">${barcodeSvg}</div>
        <div class="fine">
          <div>產地：${origin}</div>
          <div>進口商：${IMPORTER_NAME}</div>
          <div>地址：${IMPORTER_ADDRESS}</div>
        </div>
      </div>
    </div>`;
}

// localStorage keys for the persisted print-position calibration (millimetres).
export const OFFSET_X_KEY = 'nokeLabelOffsetX';
export const OFFSET_Y_KEY = 'nokeLabelOffsetY';

// Read the saved calibration offset from localStorage (defaults to 0/0).
export function readLabelOffset(): { offsetX: number; offsetY: number } {
  const readNum = (key: string): number => {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const n = parseFloat(raw || '0');
    return isNaN(n) ? 0 : n;
  };
  return { offsetX: readNum(OFFSET_X_KEY), offsetY: readNum(OFFSET_Y_KEY) };
}

export interface BuildLabelsDocumentOptions {
  /** Auto-fire window.print() on load (the print popup). Default true. */
  autoPrint?: boolean;
  /** Print-position nudge in mm applied to label CONTENT (not page size). */
  offsetX?: number;
  offsetY?: number;
}

// PURE document builder — same markup/CSS for the print popup and the preview.
// `barcodeSvgFor` renders a Code 128 payload to inline SVG markup (DOM-based in
// the browser, stub-DOM-based in the Node preview generator).
export function buildNokeLabelsDocumentHtml(
  labels: NokeLabelData[],
  barcodeSvgFor: (payload: string) => string,
  options: BuildLabelsDocumentOptions = {}
): string {
  const { autoPrint = true, offsetX = 0, offsetY = 0 } = options;

  const expanded: NokeLabelData[] = [];
  labels.forEach((label) => {
    const copies = Math.max(1, Math.floor(label.copies ?? 1));
    for (let i = 0; i < copies; i++) expanded.push(label);
  });

  const labelsHtml = expanded.map((l) => labelHtml(l, barcodeSvgFor(l.barcode))).join('');

  // NOTE: the label size is defined ONCE via the exported mm constants so it
  // prints correctly on the counter's label stock; @page margins 0 avoids the
  // browser adding page padding, and flex-wrap tiles the labels as a grid on A4.
  return `<!DOCTYPE html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8" />
<title>NOKE 標籤列印</title>
<style>
  :root {
    --label-w: ${LABEL_WIDTH_MM}mm;
    --label-h: ${LABEL_HEIGHT_MM}mm;
    /* Print-position calibration — nudges label CONTENT only, never page size. */
    --off-x: ${offsetX}mm;
    --off-y: ${offsetY}mm;
  }
  /* One label = one page on the label printer. The page is declared at the
     exact stock size; the label box is 2mm shorter to absorb driver rounding
     so content can never spill onto a second label. */
  @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, "Microsoft JhengHei", "PingFang TC", sans-serif; background: #f5f5f5; }
  .toolbar { padding: 10px; text-align: center; background: #fff; border-bottom: 1px solid #ddd; }
  .toolbar button { font-size: 14px; padding: 8px 18px; margin: 0 4px; border: none; border-radius: 4px; cursor: pointer; color: #fff; }
  .btn-print { background: #4c5e8a; }
  .btn-close { background: #888; }
  .sheet { display: flex; flex-wrap: wrap; align-content: flex-start; }
  .label {
    width: var(--label-w);
    height: var(--label-h);
    border: 1px dashed #ccc; /* screen only — removed for print */
    overflow: hidden;
    page-break-inside: avoid;
    background: #fff;
    color: #111;
  }
  /* Inner content fills the label and is shifted by the calibration offset. */
  .label-content {
    width: 100%;
    height: 100%;
    padding: 3mm 3mm 2.5mm;
    display: flex;
    flex-direction: column;
    transform: translate(var(--off-x, 0), var(--off-y, 0));
  }
  .label .name {
    font-size: 9.5pt;
    font-weight: 600;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: calc(9.5pt * 1.3); /* keep row rhythm for 1-line names */
  }
  .label .spec {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1mm;
    margin-top: 1.2mm;
    border-top: 0.15mm solid #ddd;
    padding-top: 1.2mm;
  }
  .label .spec .sku { font-size: 11.5pt; font-weight: 700; letter-spacing: 0.3pt; }
  .label .spec .variant { font-size: 9pt; color: #333; text-align: right; }
  .label .price {
    font-size: 14pt;
    font-weight: 800;
    text-align: center;
    margin: 2.6mm 0 2mm;
    letter-spacing: 0.3pt;
  }
  .label .barcode { text-align: center; }
  .label .barcode svg { width: 100%; height: 16mm; }
  .barcode-fallback { font-family: monospace; font-size: 10pt; letter-spacing: 1px; }
  .label .fine {
    margin-top: auto; /* pin compliance block to the bottom edge */
    border-top: 0.2mm solid #999;
    padding-top: 1.2mm;
    font-size: 6pt; /* 地址 line must fit 50mm width without wrapping */
    line-height: 1.55;
    color: #222;
  }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { display: block; } /* no flex tiling in print — one label per page */
    .label {
      border: none;
      height: calc(var(--label-h) - 2mm);
      page-break-after: always;
      break-after: page;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">列印</button>
    <button class="btn-close" onclick="window.close()">關閉</button>
  </div>
  <div class="sheet">${labelsHtml}</div>
  ${autoPrint
    ? `<script>
    // Barcodes are inline static SVG, so they are already painted on load;
    // the small timeout just lets layout settle before the print dialog opens.
    window.addEventListener('load', function () {
      setTimeout(function () { window.focus(); window.print(); }, 150);
    });
  </script>`
    : ''}
</body>
</html>`;
}

// Open a print popup, writing the built document into it and printing.
function openPrintWindow(html: string): void {
  const win = window.open('', '_blank', 'width=560,height=760');
  if (!win) {
    // Pop-up blocked — surface a plain alert so staff know why nothing printed.
    // eslint-disable-next-line no-alert
    alert('無法開啟列印視窗，請允許此網站的彈出視窗後再試。');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// Open a print popup containing one label per piece. The saved calibration
// offset (localStorage) is applied automatically to every print — no prompt.
export function printNokeLabels(labels: NokeLabelData[]): void {
  if (labels.length === 0) return;
  const { offsetX, offsetY } = readLabelOffset();
  const html = buildNokeLabelsDocumentHtml(labels, barcodeSvgMarkup, {
    autoPrint: true,
    offsetX,
    offsetY,
  });
  openPrintWindow(html);
}

// Build a single calibration label document: full-bleed border + centre
// crosshair + the current X/Y offset, so print alignment is visually obvious.
// Shares the exact same page/@page/label/label-content CSS as real labels so
// the offset behaves identically.
export function buildCalibrationDocumentHtml(
  offsetX: number,
  offsetY: number,
  options: { autoPrint?: boolean } = {}
): string {
  const { autoPrint = true } = options;
  const label = `X:${offsetX} Y:${offsetY}`;
  return `<!DOCTYPE html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8" />
<title>NOKE 列印校正</title>
<style>
  :root {
    --label-w: ${LABEL_WIDTH_MM}mm;
    --label-h: ${LABEL_HEIGHT_MM}mm;
    --off-x: ${offsetX}mm;
    --off-y: ${offsetY}mm;
  }
  @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, "Microsoft JhengHei", "PingFang TC", sans-serif; background: #f5f5f5; }
  .toolbar { padding: 10px; text-align: center; background: #fff; border-bottom: 1px solid #ddd; }
  .toolbar button { font-size: 14px; padding: 8px 18px; margin: 0 4px; border: none; border-radius: 4px; cursor: pointer; color: #fff; }
  .btn-print { background: #4c5e8a; }
  .btn-close { background: #888; }
  .label {
    width: var(--label-w);
    height: var(--label-h);
    border: 1px dashed #ccc; /* screen only */
    overflow: hidden;
    background: #fff;
    color: #111;
  }
  .label-content {
    position: relative;
    width: 100%;
    height: 100%;
    transform: translate(var(--off-x, 0), var(--off-y, 0));
  }
  /* Full-bleed thin border marks the label edges. */
  .calib-frame { position: absolute; inset: 1mm; border: 0.3mm solid #000; }
  /* Centre crosshair. */
  .cross-h { position: absolute; top: 50%; left: 0; width: 100%; height: 0; border-top: 0.2mm solid #000; }
  .cross-v { position: absolute; left: 50%; top: 0; height: 100%; width: 0; border-left: 0.2mm solid #000; }
  .calib-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    padding: 0.5mm 1mm;
    font-size: 11pt;
    font-weight: 700;
    letter-spacing: 0.5pt;
  }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .label { border: none; height: calc(var(--label-h) - 2mm); }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">列印</button>
    <button class="btn-close" onclick="window.close()">關閉</button>
  </div>
  <div class="label">
    <div class="label-content">
      <div class="calib-frame"></div>
      <div class="cross-h"></div>
      <div class="cross-v"></div>
      <div class="calib-text">${escapeHtml(label)}</div>
    </div>
  </div>
  ${autoPrint
    ? `<script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.focus(); window.print(); }, 150);
    });
  </script>`
    : ''}
</body>
</html>`;
}

// Print ONE calibration label at the given (unsaved) offset.
export function printCalibrationLabel(offsetX: number, offsetY: number): void {
  openPrintWindow(buildCalibrationDocumentHtml(offsetX, offsetY, { autoPrint: true }));
}
